<template>
  <div class="flex gap-2 rounded-xl bg-[#232A3A] p-1">
    <button
      v-for="option in options"
      :key="option.value"
      :class="[
        'w-full rounded-lg py-2 font-semibold transition',
        selected === option.value
          ? 'bg-[#5B8CFF] text-white'
          : 'bg-[#232A3A] text-white/80 hover:bg-[#2C3241]'
      ]"
      @click="select(option.value)"
    >
      <span class="text-lg font-bold">
        {{ option.label }}
      </span>
    </button>
  </div>
</template>

<script setup lang="ts">
import { nextTick, onMounted, ref, watch } from 'vue'

const props = defineProps<{
  modelValue: [Date | null, Date | null]
  defaultValue?: 'all' | '7d' | '30d' | '180d'
}>()
const emit = defineEmits<{
  (e: 'update:modelValue', value: [Date | null, Date | null]): void
}>()

const options = [
  { label: '7D', value: '7d' as const, days: 7 },
  { label: '30D', value: '30d' as const, days: 30 },
  { label: '180D', value: '180d' as const, days: 180 },
  { label: 'ALL', value: 'all' as const, days: null }
]

const selected = ref(props.defaultValue || '7d')
const isInitialized = ref(false)

// API 데이터가 최신 "어제" 일자까지만 제공되므로, 기준일을 항상 하루 전으로 맞춘다.
function calculateDateRange(val: 'all' | '7d' | '30d' | '180d'): [Date | null, Date] {
  // 기준이 되는 날짜를 "어제"로 설정
  const referenceDate = new Date()
  referenceDate.setDate(referenceDate.getDate() - 1)

  if (val === 'all') {
    // 전체 구간은 시작일을 제한하지 않고, 종료일만 referenceDate 로 둔다
    return [ null, referenceDate ]
  }

  const days = options.find(o => o.value === val)?.days ?? 7
  const start = new Date(referenceDate)
  start.setDate(referenceDate.getDate() - days + 1)
  return [ start, referenceDate ]
}

function select(val: 'all' | '7d' | '30d' | '180d') {
  selected.value = val
  const dateRange = calculateDateRange(val)
  emit('update:modelValue', dateRange)
}

// 초기값 설정 (렌더링 이슈 방지를 위해 onMounted에서 처리)
onMounted(async () => {
  await nextTick() // DOM이 완전히 준비될 때까지 대기
  
  // modelValue가 비어있고 defaultValue가 있으면 초기값 설정
  const [ startValue, endValue ] = props.modelValue
  if (!startValue && !endValue && props.defaultValue) {
    const dateRange = calculateDateRange(props.defaultValue)
    emit('update:modelValue', dateRange)
  }
  
  isInitialized.value = true
})

// 외부에서 값이 바뀌면 버튼도 맞춰줌 (초기화 완료 후에만)
watch(
  () => props.modelValue,
  ([ start, end ]) => {
    if (!isInitialized.value) return // 초기화 전에는 실행하지 않음
    
    if (!start) {
      selected.value = 'all'
      return
    }
    // 종료일이 지정되어 있으면 그 값을 사용하고, 없으면 오늘을 기준으로 계산
    const endDate = end ?? new Date()
    const diff = Math.round((endDate.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1
    if (diff === 7) selected.value = '7d'
    else if (diff === 30) selected.value = '30d'
    else if (diff === 180) selected.value = '180d'
    else selected.value = 'all'
  },
  { immediate: false } // immediate: false로 변경하여 초기화 시점 제어
)
</script>